import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Edit, Save, X, Plus, Trash2,
  Crown, Star, Rocket, Award, Zap, Trophy, Medal, 
  Gem, Diamond, Sparkles, Flame, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, CheckCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';

// Icon mapping for dynamic rendering
const IconMap = {
  Crown, Star, Rocket, Award, Zap, Trophy, Medal,
  Gem, Diamond, Sparkles, Fire: Flame, Bolt: Zap, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, Verified: BadgeCheck, CheckCircle
};

const MembershipsManager = () => {
  const [memberships, setMemberships] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    price: '',
    duration_days: '30',
    type: 'listing',
    features: [''],
    active: true
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchMemberships();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/boost-categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Get category info by key
  const getCategoryInfo = (typeKey) => {
    const category = categories.find(c => c.key === typeKey);
    if (category) {
      return {
        label: category.label,
        icon: category.icon,
        color: category.color
      };
    }
    // Fallback for unknown types
    return {
      label: typeKey,
      icon: 'Star',
      color: '#6B7280'
    };
  };

  // Render icon component dynamically
  const renderIcon = (iconName, className = "w-5 h-5", style = {}) => {
    const IconComponent = IconMap[iconName];
    if (IconComponent) {
      return <IconComponent className={className} style={style} />;
    }
    return <Star className={className} style={style} />;
  };

  const fetchMemberships = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/memberships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMemberships(data.memberships || []);
      }
    } catch (error) {
      console.error('Error fetching memberships:', error);
      toast({
        title: "Error",
        description: "Failed to fetch boost packages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/memberships`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...createForm,
          price: parseFloat(createForm.price),
          duration_days: parseInt(createForm.duration_days),
          features: createForm.features.filter(f => f.trim() !== '')
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Boost package created successfully",
        });
        setShowCreateModal(false);
        setCreateForm({
          name: '',
          price: '',
          duration_days: '30',
          type: 'listing',
          features: [''],
          active: true
        });
        fetchMemberships();
      } else {
        toast({
          title: "Error",
          description: "Failed to create boost package",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error creating membership:', error);
    }
  };

  const handleEdit = (membership) => {
    setEditing(membership.membership_id);
    setEditForm({
      name: membership.name,
      price: membership.price,
      duration_days: membership.duration_days,
      features: membership.features || [],
      active: membership.active
    });
  };

  const handleSave = async (membershipId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/memberships/${membershipId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Boost package updated successfully",
        });
        setEditing(null);
        fetchMemberships();
      }
    } catch (error) {
      console.error('Error updating membership:', error);
    }
  };

  const handleDelete = async (membershipId) => {
    if (!window.confirm('Are you sure you want to delete this boost package?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/memberships/${membershipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Boost package deleted successfully",
        });
        fetchMemberships();
      }
    } catch (error) {
      console.error('Error deleting membership:', error);
    }
  };

  const addFeatureField = () => {
    setCreateForm({
      ...createForm,
      features: [...createForm.features, '']
    });
  };

  const removeFeatureField = (index) => {
    setCreateForm({
      ...createForm,
      features: createForm.features.filter((_, i) => i !== index)
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...createForm.features];
    newFeatures[index] = value;
    setCreateForm({ ...createForm, features: newFeatures });
  };

  // Edit form feature helpers
  const addEditFeatureField = () => {
    setEditForm({
      ...editForm,
      features: [...(editForm.features || []), '']
    });
  };

  const removeEditFeatureField = (index) => {
    setEditForm({
      ...editForm,
      features: (editForm.features || []).filter((_, i) => i !== index)
    });
  };

  const updateEditFeature = (index, value) => {
    const newFeatures = [...(editForm.features || [])];
    newFeatures[index] = value;
    setEditForm({ ...editForm, features: newFeatures });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border dark:border-gray-700-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading boost packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold">Boost Packages</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateModal(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Create New Package
          </Button>
          <ExportButtons
            data={memberships}
            filename="boost-packages"
            columns={[
              { header: 'Package ID', key: 'membership_id', accessor: (row) => row.membership_id || 'N/A' },
              { header: 'Name', key: 'name', accessor: (row) => row.name || 'N/A' },
              { header: 'Type', key: 'type', accessor: (row) => row.type || 'N/A' },
              { header: 'Price', key: 'price', accessor: (row) => `$${row.price || 0}` },
              { header: 'Duration', key: 'duration_days', accessor: (row) => `${row.duration_days || 0} days` },
              { header: 'Active', key: 'active', accessor: (row) => row.active ? 'Yes' : 'No' },
            ]}
          />
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold dark:text-white">Create New Boost Package</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 dark:text-gray-400">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Package Name *</Label>
                  <Input
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="e.g., Premium Monthly"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price ($) *</Label>
                    <Input
                      type="number"
                      value={createForm.price}
                      onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })}
                      placeholder="29.99"
                    />
                  </div>
                  <div>
                    <Label>Duration (days) *</Label>
                    <Input
                      type="number"
                      value={createForm.duration_days}
                      onChange={(e) => setCreateForm({ ...createForm, duration_days: e.target.value })}
                      placeholder="30"
                    />
                  </div>
                </div>

                <div>
                  <Label>Package Type *</Label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {categories.length > 0 ? (
                      categories.map((cat) => (
                        <option key={cat.key} value={cat.key} className="dark:bg-gray-700 dark:text-white">
                          {cat.label}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="listing" className="dark:bg-gray-700 dark:text-white">Regular Listing</option>
                        <option value="boosted" className="dark:bg-gray-700 dark:text-white">Boosted Premium</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <Label>Features</Label>
                  {createForm.features.map((feature, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder="Feature description"
                      />
                      {createForm.features.length > 1 && (
                        <Button
                          onClick={() => removeFeatureField(index)}
                          variant="destructive"
                          size="sm"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button onClick={addFeatureField} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.active}
                    onChange={(e) => setCreateForm({ ...createForm, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label>Active (visible to trainers)</Label>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button onClick={() => setShowCreateModal(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">
                  Create Package
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Packages List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {memberships.map((membership) => {
          const categoryInfo = getCategoryInfo(membership.type);
          return (
          <Card key={membership.membership_id} className="relative">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    {editing === membership.membership_id ? (
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="text-lg"
                      />
                    ) : (
                      <>
                        {renderIcon(categoryInfo.icon, "w-5 h-5", { color: categoryInfo.color })}
                        {membership.name}
                      </>
                    )}
                  </CardTitle>
                  <Badge 
                    className="mt-2"
                    style={{ 
                      backgroundColor: categoryInfo.color,
                      color: 'white'
                    }}
                  >
                    {categoryInfo.label}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  {editing === membership.membership_id ? (
                    <>
                      <Button onClick={() => handleSave(membership.membership_id)} size="sm" variant="ghost">
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => setEditing(null)} size="sm" variant="ghost">
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => handleEdit(membership)} size="sm" variant="ghost">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDelete(membership.membership_id)} size="sm" variant="ghost" className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-gray-600 dark:text-gray-400">Price</Label>
                  {editing === membership.membership_id ? (
                    <Input
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                    />
                  ) : (
                    <p className="text-2xl font-bold text-green-600">${membership.price}</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm text-gray-600 dark:text-gray-400">Duration</Label>
                  {editing === membership.membership_id ? (
                    <Input
                      type="number"
                      value={editForm.duration_days}
                      onChange={(e) => setEditForm({ ...editForm, duration_days: parseInt(e.target.value) })}
                    />
                  ) : (
                    <p className="text-sm">{membership.duration_days} days</p>
                  )}
                </div>

                <div>
                  <Label className="text-sm text-gray-600 dark:text-gray-400">Features</Label>
                  {editing === membership.membership_id ? (
                    <div className="space-y-2 mt-2">
                      {(editForm.features || []).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            value={feature}
                            onChange={(e) => updateEditFeature(idx, e.target.value)}
                            placeholder={`Feature ${idx + 1}`}
                            className="flex-1"
                          />
                          {(editForm.features || []).length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEditFeatureField(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addEditFeatureField}
                        className="mt-2"
                      >
                        + Add Feature
                      </Button>
                    </div>
                  ) : (
                    <ul className="text-sm space-y-1 mt-2">
                      {(membership.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t">
                  <Badge variant={membership.active ? 'success' : 'secondary'}>
                    {membership.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MembershipsManager;